import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { NotPermitted, PageHeader, ReasonDialog } from '@/design';
import { useCan } from '@/domains/auth';
import { ApiError, ProblemType, SAVE_REFUSED, saveFile } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type {
  IListImport,
  IListImportColumn,
  ListImportRowFilter
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { NOT_ARRIVED } from '../../components/field-controls';
import { useListReread } from '../../components/list-area';
import { fieldsByCode } from '../../list-criteria';
import { columnReasonLabel, countOf, importStateChip, isImportWorking, listSurveyLabel } from '../../lists';
import { NEW_IMPORT } from '../../paths';
import { refusalMessage } from '../../refusal';
import { ImportStateChip } from './area';
import { NewImportForm } from './new-import';
import { ImportRows, importFieldLabel, type FieldOf } from './rows';

/**
 * One import (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §5.3) —
 * the check survey-api's Worker made of a file, read: its columns, what it found, its rows, and the
 * apply and the reversal. A record beside the imports (`AdminImportsArea`), **keyed by the import**
 * (`AdminImportRecord`), so nothing of one is drawn under another's address; `/team/import/new` is
 * the wizard (`NewImportForm`).
 *
 * **The Worker does the work; the record says how far it got.** While the import is `queued`,
 * `committing` or `reversing` (`isImportWorking`) it is read again every 3 s on a visible tab, never
 * over a read still out; nothing else polls. When its state moves the rows and the list beside are
 * read again. On `local` no Worker runs: an upload stays *Checking the file…* (§11.1).
 *
 * **Every field by its words**: the survey's catalogue is read once, for the columns' and the
 * changes' labels and formats; a code it does not name is drawn as spelled.
 *
 * **The writes** — *Apply…* (confirmed, `POST …/commit` with the rows included), *Reverse this
 * import…* (a `ReasonDialog`, the reason required — survey-api's `note`), *Result file* (`apiDownload`
 * of `…/result`). Each 202 is adopted (the import as it moved), then polled. **Refusals**: 409
 * `import-state` — another press moved it; the import is read again and its state said; 403 — a
 * restricted import, said in place of the rows and on the controls; 404 — gone, the list read
 * again; an answer that never arrived — read again, and said so.
 *
 * **A restricted import's rows, result, apply and reversal are `internal_fields.restricted`'s**
 * (survey-api's E15): for anyone else no rows are read, and the three controls are disabled with
 * `NotPermitted`'s sentence.
 */

/** survey-api's Worker reads a file in seconds; the record looks again on this beat (§5.3). */
const IMPORT_POLL_MS = 3_000;

/** survey-api's bound on a reversal's reason (`EditBatch.NoteMaxLength`). */
const REASON_MAX = 2000;

/** The route element of `/team/import/:importId` — remounted per import. */
export function AdminImportRecord() {
  const { importId = '' } = useParams();
  const location = useLocation();
  if (importId === NEW_IMPORT) {
    const survey = new URLSearchParams(location.search).get('survey') ?? '';
    return <NewImportForm key={`${NEW_IMPORT}:${survey}`} survey={survey} />;
  }
  return <ImportRecord key={importId} importId={importId} />;
}

interface ISaid {
  text: string;
  alert: boolean;
}

function ImportRecord({ importId }: { importId: string }) {
  const can = useCan();
  const rereadList = useListReread();
  // Wrapped, so a 404 (`entry: null`) is told from a read still out (`data: null`).
  const read = useAdminData(
    async (signal) => ({ entry: await listsApi.import(importId, signal) }),
    [importId]
  );
  const entry = read.data?.entry;
  const [said, setSaid] = React.useState<Nullable<ISaid>>(null);
  const [tab, setTab] = React.useState<Nullable<ListImportRowFilter>>(null);
  const [page, setPage] = React.useState(1);
  const [included, setIncluded] = React.useState<ReadonlySet<number>>(new Set());
  const [dialog, setDialog] = React.useState<Nullable<'apply' | 'reverse'>>(null);
  const [busy, setBusy] = React.useState(false);

  const state = entry?.state ?? null;
  const { reload } = read;
  const adoptRead = read.adopt;
  const adopt = React.useCallback((moved: IListImport) => adoptRead({ entry: moved }), [adoptRead]);

  // The poll: every 3 s while the Worker is at it, on a visible tab, one read at a time.
  React.useEffect(() => {
    if (state === null || !isImportWorking(state)) return;
    let out = false;
    const timer = window.setInterval(() => {
      if (out || document.visibilityState !== 'visible') return;
      out = true;
      void reload().finally(() => {
        out = false;
      });
    }, IMPORT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [state, reload]);

  // The state moved (the Worker, or a press): the list beside draws it too.
  const seen = React.useRef(state);
  React.useEffect(() => {
    if (seen.current !== null && state !== null && seen.current !== state) rereadList();
    seen.current = state;
  }, [state, rereadList]);

  // Wrapped like the import's read: `data: null` is a read still out, `catalogue: null` a survey
  // this deployment does not hold.
  const survey = entry?.survey ?? null;
  const catalogue = useAdminData(
    async (signal) => ({ survey, catalogue: survey ? await listsApi.fields(survey, signal) : null }),
    [survey]
  );
  const fields = React.useMemo(() => fieldsByCode(catalogue.data?.catalogue ?? null), [catalogue.data]);
  // The columns and the rows wait for the names, rather than drawing codes and then changing.
  const named = (catalogue.data !== null && catalogue.data.survey === survey) || catalogue.error !== null;
  const fieldOf: FieldOf = React.useCallback((code: string) => fields.get(code), [fields]);

  const presetId = entry?.presetId ?? null;
  const layout = useAdminData(
    async (signal) => ({ preset: presetId ? await listsApi.preset(presetId, signal) : null }),
    [presetId]
  );

  const plain = (body: React.ReactNode) => (
    <>
      <PageHeader title="Import" crumb="Import" />
      <div className="grid gap-4 px-6 py-6">{body}</div>
    </>
  );
  if (entry === undefined) {
    if (read.error) return plain(<LoadError message={`This import could not be read. ${read.error}`} />);
    return plain(<p className="text-sm text-muted-foreground">Reading the import…</p>);
  }
  if (entry === null) {
    // A press that found the import gone says so above the page's own answer.
    return plain(
      <>
        {said && (
          <p role={said.alert ? 'alert' : 'status'} className={said.alert ? 'text-sm text-destructive' : 'text-sm'}>
            {said.text}
          </p>
        )}
        <p role="status" className="text-sm" data-no-import>
          No such import — nothing was imported at this address.
        </p>
      </>
    );
  }

  const restrictedForMe = entry.restricted && !can('internal_fields.restricted');

  /**
   * A refusal of a press, in words; the import read again where it may have moved. The 409's
   * re-read is the record's own (`adopt`), and the sentence is what it found: the state now, the
   * import gone, or a read that failed — said with its reason, and the read asked again so the
   * record's banner says it too.
   */
  const refused = async (error: unknown, what: string): Promise<ISaid> => {
    if (error instanceof ApiError && error.status === 409 && error.problem?.type === ProblemType.importState) {
      rereadList();
      try {
        const fresh = await listsApi.import(importId);
        adoptRead({ entry: fresh });
        return {
          text: fresh
            ? `This import has moved on since it was read — it is now “${importStateChip(fresh.state).label}”, and nothing was ${what} by this press.`
            : `This import is no longer there — nothing was ${what}.`,
          alert: true
        };
      } catch (readError) {
        void reload();
        return {
          text: `This import has moved on since it was read, and nothing was ${what}. It could not be read again: ${refusalMessage(readError, 'no answer')}`,
          alert: true
        };
      }
    }
    if (error instanceof ApiError && error.status === 403) {
      return {
        text: entry.restricted
          ? 'This file writes a restricted staff field; its rows, result and apply are for holders of restricted access.'
          : 'Your Team Portal roles do not include importing files.',
        alert: true
      };
    }
    if (error instanceof ApiError && error.status === 404) {
      rereadList();
      void reload();
      return { text: 'This import is no longer there.', alert: true };
    }
    if (!(error instanceof ApiError) || error.status >= 500) {
      void reload();
      rereadList();
      return { text: `${NOT_ARRIVED} The import was read again — see its state before pressing again.`, alert: true };
    }
    return { text: refusalMessage(error, `Nothing was ${what}.`), alert: true };
  };

  const apply = async () => {
    setDialog(null);
    setBusy(true);
    setSaid(null);
    try {
      const moved = await listsApi.commit(importId, [...included].sort((a, b) => a - b));
      adopt(moved);
      setIncluded(new Set());
      setSaid({ text: 'The import is being applied.', alert: false });
    } catch (error) {
      setSaid(await refused(error, 'applied'));
    } finally {
      setBusy(false);
    }
  };

  const reverse = async (reason: string): Promise<Nullable<string>> => {
    try {
      const moved = await listsApi.reverse(importId, reason);
      adopt(moved);
      setSaid({ text: 'The import is being reversed.', alert: false });
      return null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        return refusalMessage(error, 'The reason was refused.');
      }
      const said = await refused(error, 'reversed');
      // What says the import moved, or may have, closes the dialog and is said over the record;
      // any other refusal is said in it, the reason kept for another press.
      const moved =
        !(error instanceof ApiError) ||
        error.status >= 500 ||
        error.status === 404 ||
        (error.status === 409 && error.problem?.type === ProblemType.importState);
      if (!moved) return said.text;
      setSaid(said);
      return null;
    }
  };

  const download = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const { file, fileName } = await listsApi.resultFile(importId);
      if (!saveFile(file, fileName ?? `${entry.fileName.replace(/\.[^.]+$/, '')} result.xlsx`)) {
        setSaid({ text: SAVE_REFUSED, alert: true });
      }
    } catch (error) {
      setSaid(
        error instanceof ApiError && error.status === 404
          ? { text: 'The result file is not written yet — the import is read again.', alert: true }
          : await refused(error, 'downloaded')
      );
      if (error instanceof ApiError && error.status === 404) void reload();
    } finally {
      setBusy(false);
    }
  };

  const include = (rowNos: number[], on: boolean) =>
    setIncluded((now) => {
      const next = new Set(now);
      for (const rowNo of rowNos) {
        if (on) next.add(rowNo);
        else next.delete(rowNo);
      }
      return next;
    });

  const checked = entry.state !== 'queued' && entry.state !== 'failed';
  const resultable = entry.state === 'committed' || entry.state === 'reversed';
  const counts = entry.counts;

  return (
    <>
      <PageHeader
        title={entry.fileName}
        crumb="Import"
        actions={<ImportStateChip state={entry.state} />}
        meta={<ImportFacts entry={entry} layoutName={layoutWord(entry, layout)} />}
      />
      <div className="grid min-w-0 gap-6 px-6 py-6" data-import-record={entry.id}>
        {read.error && (
          <LoadError message={`Could not refresh — showing the import as last read. ${read.error}`} />
        )}
        {said && (
          <p role={said.alert ? 'alert' : 'status'} className={said.alert ? 'text-sm text-destructive' : 'text-sm'}>
            {said.text}
          </p>
        )}
        {catalogue.error && (
          <p className="text-sm text-muted-foreground">
            The field names could not be read — the codes are drawn as spelled. {catalogue.error}
          </p>
        )}

        {entry.state === 'queued' && (
          <p role="status" className="text-sm" data-import-working>
            Checking the file… Nothing is written by a check; what it finds is listed here when it is
            done.
          </p>
        )}
        {entry.state === 'committing' && (
          <p role="status" className="text-sm" data-import-working>
            Applying… The changes are being written; the result file follows.
          </p>
        )}
        {entry.state === 'reversing' && (
          <p role="status" className="text-sm" data-import-working>
            Reversing… The values before the import are being put back.
          </p>
        )}
        {entry.state === 'failed' && (
          <Alert variant="destructive">
            <AlertDescription data-import-error>
              The file could not be checked: {entry.error ?? 'no reason was given.'}
            </AlertDescription>
          </Alert>
        )}

        {!named && (entry.columns.length > 0 || checked) && (
          <p className="text-sm text-muted-foreground">Reading the field names…</p>
        )}
        {named && entry.columns.length > 0 && <ImportColumns entry={entry} fieldOf={fieldOf} />}

        {checked && (
          <section className="grid gap-1" aria-labelledby="import-found-title">
            <h2 id="import-found-title" className="text-base font-semibold">
              What the check found
            </h2>
            <p data-import-counts>
              {countOf(counts.rows, 'row')} · {counts.matched.toLocaleString('en-US')} matched ·{' '}
              {counts.unmatched.toLocaleString('en-US')} match nobody ·{' '}
              {counts.errors.toLocaleString('en-US')} not written ·{' '}
              {counts.changed.toLocaleString('en-US')} would change ·{' '}
              {counts.held.toLocaleString('en-US')} held as unusual
            </p>
          </section>
        )}

        {checked && named && (
          <ImportRows
            entry={entry}
            fieldOf={fieldOf}
            tab={tab}
            onTab={(next) => {
              setTab(next);
              setPage(1);
            }}
            page={page}
            onPage={setPage}
            included={included}
            onInclude={include}
            rereads={entry.state}
            restricted={restrictedForMe}
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          {entry.state === 'ready' && (
            <Button
              type="button"
              disabled={busy || restrictedForMe || counts.changed === 0}
              aria-describedby={restrictedForMe ? 'import-restricted' : undefined}
              onClick={() => setDialog('apply')}
            >
              Apply the changes of {countOf(counts.changed, 'row')}…
            </Button>
          )}
          {entry.state === 'ready' && counts.changed === 0 && (
            <span className="text-sm text-muted-foreground">Nothing in this file would change a value.</span>
          )}
          {resultable && (
            <Button
              type="button"
              variant="outline"
              disabled={busy || restrictedForMe}
              aria-describedby={restrictedForMe ? 'import-restricted' : undefined}
              onClick={() => void download()}
            >
              Result file
            </Button>
          )}
          {entry.state === 'committed' && (
            <Button
              type="button"
              variant="outline"
              disabled={busy || restrictedForMe}
              aria-describedby={restrictedForMe ? 'import-restricted' : undefined}
              onClick={() => setDialog('reverse')}
            >
              Reverse this import…
            </Button>
          )}
          {restrictedForMe && (checked || resultable) && (
            <NotPermitted id="import-restricted" what="reading or writing restricted staff fields" />
          )}
        </div>
      </div>

      <AlertDialog open={dialog === 'apply'} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply the changes of {countOf(counts.changed, 'row')} to {listSurveyLabel(entry.survey)}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {counts.held > 0 &&
                `${countOf(counts.held, 'row')} ${counts.held === 1 ? 'holds' : 'hold'} a change kept unwritten unless included — ${included.size.toLocaleString('en-US')} included. `}
              Every change is recorded against this file and can be reversed as one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void apply()}>Apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReasonDialog
        open={dialog === 'reverse'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={`Reverse ${entry.fileName}?`}
        description="Every value this import wrote is put back as it was before it. A value edited since the import is kept, and listed as kept. The reason is recorded with the reversal on each history."
        reasonLabel="Why it is reversed"
        required
        max={REASON_MAX}
        confirmLabel="Reverse the import"
        onConfirm={reverse}
      />
    </>
  );
}

/** The layout's name, read once; one since deleted is said so. */
function layoutWord(
  entry: IListImport,
  layout: { data: Nullable<{ preset: Nullable<{ body: { name: string } }> }>; error: Nullable<string> }
): Nullable<string> {
  if (entry.presetId === null) return null;
  if (layout.error) return 'a layout that could not be read';
  if (layout.data === null) return 'reading…';
  return layout.data.preset?.body.name ?? 'a layout since deleted';
}

/** Who checked it and when, its layout and its note; who applied and reversed it. */
function ImportFacts({ entry, layoutName }: { entry: IListImport; layoutName: Nullable<string> }) {
  return (
    <div className="grid gap-0.5 text-sm text-muted-foreground">
      <span>
        {listSurveyLabel(entry.survey)} · By {entry.createdBy.email}, {formatDateTime(entry.createdAt)}
        {layoutName && ` · Layout: ${layoutName}`}
        {entry.note && ` · “${entry.note}”`}
      </span>
      {entry.committedAt && (
        <span>
          Applied by {entry.committedBy?.email ?? 'an account since removed'}, {formatDateTime(entry.committedAt)}
        </span>
      )}
      {entry.reversedAt && (
        <span>
          Reversed by {entry.reversedBy?.email ?? 'an account since removed'}, {formatDateTime(entry.reversedAt)}
        </span>
      )}
    </div>
  );
}

/** Each heading of the file and what it writes — the field in words, the key named, or why it
 *  writes nothing. */
function ImportColumns({ entry, fieldOf }: { entry: IListImport; fieldOf: FieldOf }) {
  const said = (column: IListImportColumn): string => {
    if (column.reason !== null) return columnReasonLabel(column.reason);
    if (column.field !== null && entry.keyColumn !== null && isKey(column, entry.keyColumn)) {
      return 'matches the record';
    }
    return column.field === null ? 'writes nothing' : importFieldLabel(column.field, fieldOf);
  };
  return (
    <section className="grid gap-2" aria-labelledby="import-columns-title">
      <h2 id="import-columns-title" className="text-base font-semibold">
        Columns
      </h2>
      <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm" data-import-columns>
        {entry.columns.map((column, index) => (
          <li key={index} data-import-column={column.heading} data-skipped={column.reason !== null || undefined}>
            <span className="font-medium">{column.heading || '(no heading)'}</span>{' '}
            <span className={column.reason !== null ? 'text-muted-foreground' : undefined}>→ {said(column)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The key column: `sys.participant_id` for *Internal ID*, `sys.crd` for *CRD* — the codes
 *  `ListImportColumns.Map` gives them, the column that writes nothing and has no reason. */
function isKey(column: IListImportColumn, keyColumn: string): boolean {
  return keyColumn === 'CRD' ? column.field === 'sys.crd' : column.field === 'sys.participant_id';
}
